import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO, faqSchema } from '../../../components/SEO';
import { FAQSection } from '../../services/PillarPage';
import {
  ArrowLeft, ShieldCheck, ExternalLink, ChevronDown, Check,
  Wrench, PackagePlus, Palette, ShoppingCart, FileSearch,
  ClipboardList, BarChart2,
} from 'lucide-react';

const UPP_AMBER = '#D97706';

/* ─── Data ─────────────────────────────────────────────────────── */
const features = [
  {
    id: 1, icon: ShieldCheck, color: '#D97706', bg: '#FFFBEB',
    title: 'Protection Plans',
    desc: 'Sell 1–5 year extended warranties priced as a fixed amount, a percentage of the product price, or by price band. Coverage can start after the manufacturer warranty ends, with optional claim limits.',
  },
  {
    id: 2, icon: Wrench, color: '#1A73E8', bg: '#EBF4FF',
    title: 'Services & Installation Booking',
    desc: 'Sell installation, assembly and setup. Shoppers pick a preferred date, add notes and enter their postal code — you control lead times, closed days and service areas.',
  },
  {
    id: 3, icon: PackagePlus, color: '#059669', bg: '#ECFDF5',
    title: 'Product Add-ons',
    desc: 'Offer accessories from your existing catalog right next to the main product, so the perfect add-on is always one tap away.',
  },
  {
    id: 4, icon: Palette, color: '#7C3AED', bg: '#F3EEFF',
    title: 'A Widget You Design',
    desc: 'Choose from 4 layouts, match your colors and wording, and see every change in a live preview. Translated into 11 languages and works with Online Store 2.0 and vintage themes.',
  },
  {
    id: 5, icon: ShoppingCart, color: '#DC2626', bg: '#FEF2F2',
    title: 'Second-Chance Offers',
    desc: 'An add-to-cart popup and one-tap protection on the cart page catch items added without a plan. Plans carry through "Buy it now" checkouts too.',
  },
  {
    id: 6, icon: FileSearch, color: '#0891B2', bg: '#EFF6FF',
    title: 'Customer Protection Page',
    desc: 'Every plan gets a PRT- or SVC- reference number. Customers look up their coverage, file claims with photos, message you and print plan details — all self-serve.',
  },
  {
    id: 7, icon: ClipboardList, color: '#D97706', bg: '#FFFBEB',
    title: 'Claims & Installations',
    desc: 'A claims inbox with photos, outcomes, costs and one-click free replacement orders, plus an installation pipeline with installers, scheduling and overdue flags.',
  },
  {
    id: 8, icon: BarChart2, color: '#059669', bg: '#ECFDF5',
    title: 'Analytics & Automation',
    desc: 'Revenue, attach rate and loss ratio per offer. Automatic emails, auto-fulfilment of plan lines, refunds that cancel plans, Shopify Flow triggers and CSV export.',
  },
];

const steps = [
  { step: '01', label: 'Create an offer', desc: 'Start from a ready-made template for a protection plan, service or add-on.' },
  { step: '02', label: 'Enable the widget', desc: 'Switch on "Upsell widget" under App embeds in your theme editor.' },
  { step: '03', label: 'Go live', desc: 'Switch the offer to Live — it appears on the products you assigned.' },
  { step: '04', label: 'Test it', desc: 'Open a product page and add a plan to your cart. That\'s it.' },
];

const plans = [
  {
    name: 'Free', price: '$0', period: '',
    revenue: '$300', offers: '1',
    intro: 'Includes:',
    items: ['Fixed pricing', 'Product page & cart widget', 'Customer protection page'],
  },
  {
    name: 'Starter', price: '$19', period: '/mo',
    revenue: '$3,000', offers: '5',
    intro: 'Everything in Free, plus:',
    items: ['Percentage-of-price pricing', 'Online claims'],
  },
  {
    name: 'Growth', price: '$49', period: '/mo', popular: true,
    revenue: '$15,000', offers: '20',
    intro: 'Everything in Starter, plus:',
    items: ['Price-band pricing', 'Claim photos', 'Appointment booking', 'Installer scheduling'],
  },
  {
    name: 'Pro', price: '$99', period: '/mo',
    revenue: 'Unlimited', offers: 'Unlimited',
    intro: 'Everything in Growth, plus:',
    items: ['Shopify Flow triggers', 'CSV exports', 'No "Powered by Upsell" badge'],
  },
];

const faqs = [
  {
    question: 'Does Upsell Plans Pro work on the Basic Shopify plan?',
    answer: 'Yes. It works on every Shopify plan, from Basic to Shopify Plus. The app uses no Plus-only APIs, so every feature is available regardless of your Shopify plan.',
  },
  {
    question: 'Am I the insurer?',
    answer: 'Upsell Plans Pro is software, not an insurance company. You, the merchant, are the provider of the protection plans and services you sell — you set the terms and honor the claims. The app handles selling, registering and tracking every plan, and gives you the tools to manage claims and installations. Make sure your plans follow the consumer and warranty laws where you sell.',
  },
  {
    question: 'What happens if I go over my plan\'s revenue limit?',
    answer: 'Your widget keeps selling — you never lose a sale. If you stay over the limit for 14 days, a small "Powered by Upsell" badge appears on the widget until you upgrade or your limit resets on the 1st of the month.',
  },
  {
    question: 'Will the plan products show up in my collections or search?',
    answer: 'No. The products the app creates for plans and services are unlisted, so they never appear in your collections, storefront search or product recommendations. They only show up inside the widget and the cart.',
  },
  {
    question: 'Can customers file claims themselves?',
    answer: 'Yes. Every plan gets a reference number, and customers use it on your store\'s protection page to look up their coverage, message you and print plan details. Online claims are included from the Starter plan, and claim photos from the Growth plan.',
  },
  {
    question: 'Which themes does it work with?',
    answer: 'All of them. On Online Store 2.0 themes you can place the product-page and cart blocks anywhere in the theme editor; on vintage themes the app embed injects the widget automatically.',
  },
];

/* ─── Shared dark section bg ────────────────────────────────────── */
const DarkBg = ({ children, className = '' }) => (
  <section className={`relative overflow-hidden ${className}`} style={{ background: '#0B1426' }}>
    {/* Grid lines */}
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: 'linear-gradient(rgba(217,119,6,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(217,119,6,0.07) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
    }} />
    {/* Orbs */}
    <div style={{ position: 'absolute', top: '-20%', right: '-10%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(217,119,6,0.14) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: '-15%', left: '-5%', width: 420, height: 420, background: 'radial-gradient(circle, rgba(37,99,235,0.09) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div className="relative z-10">{children}</div>
  </section>
);

/* ─── Top Bar ───────────────────────────────────────────────────── */
const UpsellTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to site
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: UPP_AMBER }}>
          <ShieldCheck size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Upsell Plans Pro</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

/* ─── Footer Strip ──────────────────────────────────────────────── */
const UpsellFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Upsell Plans Pro · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/upsell-plans-pro/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/upsell-plans-pro/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

/* ─── Section heading ───────────────────────────────────────────── */
const SectionHeading = ({ label, children, sub }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="mb-12"
  >
    <div className="flex items-center gap-3 mb-6">
      <span className="section-label">{label}</span>
      <span className="w-6 h-px bg-[#2563EB]/30" />
    </div>
    <h2
      className="font-display font-bold text-[#0F172A]"
      style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
    >
      {children}
    </h2>
    {sub && <p className="text-[#374151] max-w-md mt-4 text-sm leading-relaxed">{sub}</p>}
  </motion.div>
);

/* ─── Page ──────────────────────────────────────────────────────── */
export const UpsellPlansProFeaturesPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Upsell Plans Pro — Protection Plans, Installation & Add-ons for Shopify"
        description="Upsell Plans Pro lets Shopify merchants sell extended warranties, installation services and product add-ons on every product page. Plans are registered automatically and customers file claims on a self-serve page."
        path="/apps/upsell-plans-pro"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Upsell Plans Pro',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Shopify',
            description: 'Upsell Plans Pro is a Shopify app for selling extended warranties, paid services such as installation and assembly, and product add-ons on the product page, with automatic plan registration, a customer claims portal and installation scheduling.',
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: 'USD',
              lowPrice: '0',
              highPrice: '99',
              offerCount: plans.length,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Smart Solutions',
              url: 'https://smartsolution.ai',
            },
          },
          faqSchema(faqs),
        ]}
      />
      <UpsellTopBar />

      {/* ── HERO ── */}
      <DarkBg>
        <div className="container-custom py-28 md:py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            {/* Badge */}
            <div className="flex items-center gap-3 mb-8">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 border text-[0.65rem] font-mono tracking-widest uppercase"
                style={{ borderColor: 'rgba(217,119,6,0.35)', color: UPP_AMBER, backgroundColor: 'rgba(217,119,6,0.08)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ backgroundColor: UPP_AMBER }} />
                Protection Plans · Services · Add-ons
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(217,119,6,0.3)' }} />
            </div>

            {/* Headline */}
            <h1
              className="font-display font-bold text-white mb-6 leading-[1.05]"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              Every Product,{' '}
              <span className="italic" style={{ color: UPP_AMBER }}>Protected</span>.
            </h1>

            {/* Subtitle */}
            <p
              className="text-[#94A3B8] leading-relaxed mb-10 max-w-xl"
              style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}
            >
              Sell extended warranties, installation services and product add-ons right on the
              product page. Every plan is registered and tracked automatically, and customers check
              coverage and file claims on a self-serve page — high-margin revenue, without the paperwork.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-16">
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#95BF47' }}
              >
                <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
                Add to Shopify
              </a>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border transition-colors hover:border-white/40 hover:text-white"
                style={{ borderColor: 'rgba(148,163,184,0.4)', color: '#94A3B8' }}
              >
                Explore Features
                <ChevronDown size={14} />
              </a>
            </div>

            {/* Stats */}
            <div
              className="grid grid-cols-3 gap-8 pt-8"
              style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}
            >
              {[
                { num: '8', label: 'Core Features' },
                { num: '< 5 min', label: 'Setup Time' },
                { num: 'Free plan', label: 'To Get Started' },
              ].map(({ num, label }) => (
                <div key={label}>
                  <div
                    className="font-display font-bold text-white mb-1"
                    style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)' }}
                  >
                    {num}
                  </div>
                  <div className="font-mono text-[0.6rem] tracking-widest text-[#64748B] uppercase">{label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom fade */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
          background: 'linear-gradient(to bottom, transparent, #F8F7F4)',
          pointerEvents: 'none',
        }} />
      </DarkBg>

      {/* ── HOW IT WORKS ── */}
      <section className="section-padding bg-[#F8F7F4] pb-0">
        <div className="container-custom">
          <SectionHeading
            label="How It Works"
            sub="Four steps from install to your first protected order — about five minutes, no theme code."
          >
            Live in{' '}
            <span className="italic text-gold">five minutes</span>.
          </SectionHeading>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white border border-[#E2E8F0] p-7"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
              >
                <span className="font-mono text-[0.65rem] tracking-widest uppercase" style={{ color: UPP_AMBER }}>
                  Step {s.step}
                </span>
                <h3 className="font-display text-lg font-bold text-[#0F172A] mt-3 mb-2">{s.label}</h3>
                <p className="text-[#374151] text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="section-padding bg-[#F8F7F4]">
        <div className="container-custom">
          <SectionHeading
            label="What Upsell Plans Pro Does"
            sub="Eight features that turn every product page into a protection, service and add-on counter — and handle everything after the sale."
          >
            Sell more than{' '}
            <span className="italic text-gold">the product</span>.
          </SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map((feat, i) => (
              <motion.div
                key={feat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white border border-[#E2E8F0] p-8 group hover:border-[#C7D2FE] transition-all duration-300"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center shrink-0 border border-[#E2E8F0]"
                    style={{ backgroundColor: feat.bg }}
                  >
                    <feat.icon size={22} style={{ color: feat.color }} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#2563EB] transition-colors duration-300">
                      {feat.title}
                    </h3>
                    <p className="text-[#374151] text-sm leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="section-padding bg-white border-t border-[#E2E8F0]">
        <div className="container-custom">
          <SectionHeading
            label="Pricing"
            sub="Start free and upgrade as your upsell revenue grows. No commission on any plan — you keep 100% of what you sell."
          >
            Simple plans,{' '}
            <span className="italic text-gold">zero commission</span>.
          </SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="relative bg-white border p-7 flex flex-col"
                style={{
                  borderColor: plan.popular ? UPP_AMBER : '#E2E8F0',
                  boxShadow: plan.popular ? '0 16px 48px rgba(217,119,6,0.14)' : '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                {plan.popular && (
                  <span
                    className="absolute -top-3 left-7 px-2.5 py-1 text-[0.6rem] font-mono tracking-widest uppercase text-white"
                    style={{ backgroundColor: UPP_AMBER }}
                  >
                    Most Popular
                  </span>
                )}

                <h3 className="font-display text-xl font-bold text-[#0F172A] mb-3">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-display font-bold text-[#0F172A]" style={{ fontSize: '2.25rem' }}>{plan.price}</span>
                  <span className="text-[#374151] text-sm">{plan.period}</span>
                </div>

                <div className="space-y-2 pb-5 mb-5 border-b border-[#E2E8F0]">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#374151]">Upsell revenue / mo</span>
                    <span className="font-semibold text-[#0F172A]">{plan.revenue}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#374151]">Live offers</span>
                    <span className="font-semibold text-[#0F172A]">{plan.offers}</span>
                  </div>
                </div>

                <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase mb-3">{plan.intro}</p>
                <ul className="space-y-2.5">
                  {plan.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[#374151]">
                      <Check size={15} className="shrink-0 mt-0.5" style={{ color: UPP_AMBER }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 text-sm text-[#374151]"
          >
            {[
              ['14-day free trial', 'Every paid plan starts with a 14-day free trial, billed through Shopify.'],
              ['Never lose a sale', 'Over your limit? The widget keeps selling. After 14 days a small "Powered by Upsell" badge appears until you upgrade.'],
              ['Monthly reset', 'Upsell revenue limits reset on the 1st of every month.'],
            ].map(([title, desc]) => (
              <div key={title} className="border-l-2 pl-4" style={{ borderColor: UPP_AMBER }}>
                <p className="font-semibold text-[#0F172A] mb-1">{title}</p>
                <p className="leading-relaxed">{desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <FAQSection faqs={faqs} theme={{ primary: UPP_AMBER }} title="Upsell Plans Pro FAQs" />

      {/* ── CTA ── */}
      <DarkBg className="py-24">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(217,119,6,0.4)' }} />
              <span
                className="font-mono text-[0.65rem] tracking-[0.25em] uppercase"
                style={{ color: UPP_AMBER }}
              >
                Get Started
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(217,119,6,0.4)' }} />
            </div>

            <h2
              className="font-display font-bold text-white mb-6 leading-[1.1]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}
            >
              Sell the protection,{' '}
              <span className="italic" style={{ color: UPP_AMBER }}>not just the product</span>.
            </h2>

            <p className="text-[#94A3B8] mb-10 leading-relaxed max-w-lg mx-auto">
              Add protection plans, installation and add-ons to your product pages today — free to start,
              no commission, live in minutes.
            </p>

            <a
              href="#"
              className="inline-flex items-center gap-2.5 px-8 py-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#95BF47' }}
            >
              <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
              Add to Shopify — Free Plan Available
            </a>
          </motion.div>
        </div>
      </DarkBg>

      <UpsellFooter />
    </div>
  );
};
